import { P2GBox, P2GText } from '@parcel2go/core-ui';
import { InitialiseCustomerAction } from '~/apps/smartsend/lib/context/SmartSendContextModels';
import { SidePanelContent } from '~/components/sidepanel-section';
import { AddCollectionAddress } from '../../../components/add-collection-address';
import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { SmartSendContext } from '~/apps/smartsend/lib/context/SmartSendContext';
import { useSmartSendApi } from '~/lib/api/smartsend/use-smartsend-api';
import { Customer } from '~/lib/api/smartsend/types';
import { ConfigSetting, Trans, useTenant, useTranslation } from '@parcel2go/npm-p2g-app';
import { useAuth } from 'react-oidc-context';
import { SmartSendProvider } from '~/lib/consts/providers';
import Loading from '~/components/loading/Loading';
import { OnboardingActionBar } from '../intro/onboarding-action-bar';
import { OnboardingTitle } from '../rules/onboarding-title';
import OnboardingScreenSpacing from '../spacing-consts';
import { OnboardingScreenContentWrapper } from '../../OnboardingScreenContentWrapper';
import { TranslationNamespaces } from '~/lib/constants';

type props = {
  isXs: boolean;
  onDone: (customer: Customer | null, justAdded: boolean) => void;
};

const ProfileScreen = ({ isXs, onDone }: props) => {
  const { t } = useTranslation(TranslationNamespaces.SMARTSEND);
  const { settings } = useTenant();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const { dispatch } = useContext(SmartSendContext);
  const { createDefaultCustomer } = useSmartSendApi();
  const { getCustomers } = useSmartSendApi();

  const customer = useMemo<Customer>(() => {
    return {
      CountryIsoCode: settings[ConfigSetting.DEFAULT_ORIGIN_COUNTRY],
      CountrySubdivision: undefined,
      AccountName: 'Default',
      Name: user?.profile?.name || '',
      Postcode: '',
      Email: user?.profile?.email || '',
      Phone: user?.profile?.phone_number || '',
      Town: '',
      AddressLine1: '',
      AddressLine2: '',
      Provider: SmartSendProvider,
      VatNumber: '',
      Meta: {}
    };
  }, [settings, user]);

  useEffect(() => {
    const fetchCustomers = async () => {
      try {
        const customers = await getCustomers();
        if (customers.result.Customers.length > 0) {
          const customerToUse =
            customers.result.Customers.find((x) => x.AccountName === 'Default') || customers.result.Customers[0];
          setTimeout(() => {
            onDone(customerToUse, false);
          }, 2000);
        } else {
          setLoading(false);
        }
      } catch (error) {
        console.error('Error fetching customers:', error);
      }
    };
    fetchCustomers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const complete = useCallback(
    async (customer: Customer) => {
      const res = await createDefaultCustomer(customer);
      dispatch(new InitialiseCustomerAction(res.result.Customer));
      onDone(res.result.Customer, true);
    },
    [createDefaultCustomer, dispatch, onDone]
  );

  if (loading) {
    return <Loading />;
  }

  return (
    <OnboardingScreenContentWrapper>
      <P2GBox textAlign="center">
        <SidePanelContent>
          <OnboardingTitle text="Add your collection <span>address</span>" translationKey="SETUP_PROFILE_TITLE" />
          <P2GText>
            <Trans
              i18nKey="SETUP_PROFILE_SUBTITLE"
              ns={TranslationNamespaces.SMARTSEND}
              defaults={`We’ll use these details for your collections and<br />to find your local drop off points.`}
            />
          </P2GText>
        </SidePanelContent>
      </P2GBox>
      <P2GBox sx={OnboardingScreenSpacing}>
        <AddCollectionAddress
          creating={false}
          maxWidth="xs"
          isXs={isXs}
          finish={complete}
          initialCustomer={customer}
          buttonWrapper={(b) => (
            <OnboardingActionBar next={b}>
              {!isXs && (
                <>
                  <P2GText variant="subtitle2" component="div">
                    {t('SETUP_PROFILE_ACTION_BAR', "Let's get your address set up")}
                  </P2GText>
                  <P2GText>
                    {t(
                      'SETUP_PROFILE_ACTION_BAR_SUBTITLE',
                      'We’ll use these details for your collections and to find your local drop off points.'
                    )}
                  </P2GText>
                </>
              )}
            </OnboardingActionBar>
          )}
        />
      </P2GBox>
    </OnboardingScreenContentWrapper>
  );
};
export default ProfileScreen;
